import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '../components/header';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/auth-context';
import { AlertCircle, ChevronUp, FileText, Flag, Info, Inspect, Users } from 'lucide-react';
import {
  Building,
  Shield,
  User,
  Globe,
  Phone,
  Award,
  AlertTriangle,
  Clock,
  Calendar,
  Check,
  ChevronDown,
  Star,
  MapPin,
} from 'lucide-react';
import { useTranslationWithLanguage } from '../context/language-context';
import { InspectionQuestionnaire } from '@/components/inspection-questionnaire';
import InspectionQuestionnairePage from './InspectionQuestionnairePage';
import { Card } from '@/components/ui/card';
import EstablishmentAccordionPage from './EstablishmentAccordion';

interface ExpandedSections {
  wps: boolean;
  workerInjury: boolean;
  employeesAbsconded: boolean;
  ownerDetails: boolean;
  workersDetails: boolean;
  nationalities: boolean;
  contactDetails: boolean;
  taqyeemDetails: boolean;
  riskReasons: boolean;
  previousVisits: boolean;
}

// Define Task interface based on the sample data
interface TaskItem {
  id: string;
  establishment_id: string;
  establishment_name_en: string;
  establishment_name_ar: string;
  establishment: {
    id: string;
    name_en: string;
    name_ar: string;
    license_no: string;
    license_expiry_date: string;
    license_issue_date: string;
    establishment_status_en: string;
    establishment_status_ar: string;
    sector_en: string | null;
    sector_ar: string | null;
    class_id: string;
    [key: string]: any;
  };
  [key: string]: any;
}

interface WPSPayment {
  allowances: string;
  amount: string;
  card_no: string;
  contract_salary: string;
  date_of_birth: string;
  eid_number: string;
  establishment_id: string;
  fixed_income: string;
  full_name_ar: string;
  person_name_en: string;
  id: string;
  paid_less: string;
  person_id: string;
  refund_amount: string;
  ymonth: string;
  [key: string]: any; // For additional fields
}

interface WorkerInjury {
  case_source: string | null;
  case_status: number | null;
  compensation_amount: string | null;
  date_of_birth: string;
  doctor_visited: boolean;
  eid_number: string | null;
  establishment_id: string;
  full_name_ar: string;
  person_name_en: string;
  id: string;
  incident_date: string;
  incident_description: string;
  incident_location_address: string | null;
  injury_part: string | null;
  person_id: string;
  [key: string]: any; // For additional fields
}

const EstablishmentDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { t, language } = useTranslationWithLanguage();
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  const [isBottomSheetExpanded, setIsBottomSheetExpanded] = useState(false);
  const [bottomSheetHeight, setBottomSheetHeight] = useState('70%');
  const [taskList, setTaskList] = useState<TaskItem[]>([]);
  // State for task data
  const [loading, setLoading] = useState<boolean>(true);
  const [currentTask, setCurrentTask] = useState<TaskItem | null>(
    null
  );

  const [questionnaireOpen, setQuestionnaireOpen] = useState<boolean>(false);
  const [wpsPayments, setWpsPayments] = useState<WPSPayment[]>([]);
  const [workerInjuries, setWorkerInjuries] = useState<
    WorkerInjury[]
  >([]);
  const [wpsCompliance, setWpsCompliance] = useState<{
    people_paid_percentage: number;
    wage_paid_percentage: string | number;
    paid_people: number;
    total_people: number;
    paid_wage_amount: string | number;
    total_wage_amount: string | number;
    percentage_payment?: number;
  } | null>(null);
  const [employeesAbsconded, setEmployeesAbsconded] = useState<any[]>(
    []
  );

  // State for data table
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedColumns, setSelectedColumns] = useState<{
    [key: string]: { [key: string]: boolean };
  }>({
    wps: {
      person_name_en: true,
      person_name_ar: true,
      compliance: false,
      paid: false,
      total_to_be_paid: false,
      percentage_payment: true,
      ymonth: true,
    },
    workerInjury: {
      full_name_en: true,
      incident_date: true,
      incident_description: true,
      doctor_visited: true,
      task_status_display: true,
      compensation_amount: true,
    },
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    wps: {
      currentPage: 1,
      pageSize: 10,
      total: 0,
    },
    workerInjury: {
      currentPage: 1,
      pageSize: 10,
      total: 0,
    },
  });

  // State for expanded sections
  const [expandedSections, setExpandedSections] =
    useState<ExpandedSections>({
      wps: true,
      workerInjury: false,
      employeesAbsconded: false,
      ownerDetails: false,
      workersDetails: false,
      nationalities: false,
      contactDetails: false,
      taqyeemDetails: false,
      riskReasons: false,
      previousVisits: false,
    });

  // State for active tab in bottom sheet
  const [activeTab, setActiveTab] = useState<'establishment' | 'questionnaire'>('establishment');

  // Toggle section expansion
  const toggleSection = (section: keyof ExpandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Expand all sections
  const expandAll = () => {
    const allExpanded = Object.keys(expandedSections).reduce(
      (acc, key) => {
        acc[key as keyof ExpandedSections] = true;
        return acc;
      },
      {} as ExpandedSections
    );

    setExpandedSections(allExpanded);
  };

  // Collapse all sections
  const collapseAll = () => {
    const allCollapsed = Object.keys(expandedSections).reduce(
      (acc, key) => {
        acc[key as keyof ExpandedSections] = false;
        return acc;
      },
      {} as ExpandedSections
    );

    setExpandedSections(allCollapsed);
  };

  // Get auth token from OIDC
  const getAuthToken = () => {
    return getAccessToken() || '';
  };

  useEffect(() => {
      const event = new CustomEvent('bottomSheetStateChange', {
        detail: { isExpanded: isBottomSheetExpanded },
      });
      window.dispatchEvent(event);
    }, [isBottomSheetExpanded]);

  // Fetch task data
  useEffect(() => {
    const fetchTaskData = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const tid = localStorage.getItem('tid');
        const tasks = localStorage.getItem('taskIds');
        if (tasks && tasks.length) {
          const taskIds = JSON.parse(tasks);
          setTaskList(taskIds);
          console.log(taskIds[0]);
          setCurrentTask(taskIds[0]);
        } else {
          const taskUrl = `https://services-sdp-dev.mohre.gov.ae/jsonapi/transform/task_list?id=${tid}`;
          const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAccessToken()}`,
          };
          const taskResponse = await fetch(taskUrl, {
            headers,
          });
          if (taskResponse.ok) {
            const tasksData = await taskResponse.json();
            console.log('Tasks response:', tasksData);
            if (tasksData.status && tasksData.data) {
              // Set the first task as selected by default if available
              if (tasksData.data.length > 0) {
                setCurrentTask(tasksData.data[0]);
                console.log('Current task set:', tasksData.data[0]);
              }
            } else {
              console.error('No tasks data found in response');
            }
          } else {
            console.error(
              'Failed to fetch tasks:',
              taskResponse.status
            );
          }
        }
      } catch (error) {
        console.error('Error fetching task data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTaskData();
  }, [id]);

  // Fetch WPS compliance data
  useEffect(() => {
    const fetchWpsCompliance = async () => {
      if (!id) return;
      localStorage.setItem('establishment_id', id);

      try {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        };

        const wpsCondition = JSON.stringify({
          rules: [
            { field: 'establishment_id', operator: 'eq', value: id },
          ],
        });

        const wpsResponse = await fetch(
          `https://services-sdp-dev.mohre.gov.ae/sdp/api/v1/vw_WPSEstablishmentMetrics?condition=${encodeURIComponent(
            wpsCondition
          )}`,
          { headers }
        );

        if (wpsResponse.ok) {
          const wpsData = await wpsResponse.json();
          console.log('WPS establishment metrics response:', wpsData);
          if (
            wpsData.success &&
            wpsData.data &&
            wpsData.data.length > 0
          ) {
            // Format metrics from the new API to match the expected structure
            const metrics = wpsData.data[0];
            // Calculate derived values for the UI components
            const formattedData = {
              total_people:
                metrics.total_compliant +
                metrics.total_non_compliant +
                metrics.total_exempt,
              paid_people:
                metrics.total_compliant + metrics.total_exempt,
              people_paid_percentage: parseFloat(
                metrics.percentage_compliant
              ),
              total_wage_amount: parseFloat(metrics.total_to_be_paid),
              paid_wage_amount: parseFloat(metrics.total_paid),
              wage_paid_percentage: parseFloat(
                metrics.percentage_paid
              ),
              // Calculate percentage_payment from the available data if needed
              percentage_payment: parseFloat(metrics.percentage_paid), // Use percentage_paid as fallback
            };
            setWpsCompliance(formattedData);
            console.log('WPS compliance set:', formattedData);
          } else {
            console.error('No WPS compliance data found in response');
          }
        } else {
          console.error(
            'Failed to fetch WPS compliance:',
            wpsResponse.status
          );
        }
      } catch (error) {
        console.error('Error fetching WPS compliance:', error);
      }
    };

    fetchWpsCompliance();
  }, [id]);

  // Fetch employees absconded data
  useEffect(() => {
    const fetchEmployeesAbsconded = async () => {
      if (!id) return;

      try {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        };

        const condition = JSON.stringify({
          rules: [
            { field: 'person_status_id', operator: 'eq', value: '3' },
          ],
        });

        const response = await fetch(
          `https://services-sdp-dev.mohre.gov.ae/sdp/api/v1/person?condition=${encodeURIComponent(
            condition
          )}`,
          { headers }
        );

        if (response.ok) {
          const data = await response.json();
          console.log('Employees absconded response:', data);
          if (data.success && data.data) {
            setEmployeesAbsconded(data.data);
            console.log('Employees absconded set:', data.data);
          } else {
            console.error(
              'No employees absconded data found in response'
            );
          }
        } else {
          console.error(
            'Failed to fetch employees absconded:',
            response.status
          );
        }
      } catch (error) {
        console.error('Error fetching employees absconded:', error);
      }
    };

    fetchEmployeesAbsconded();
  }, [id]);

  // Fetch WPS payments with pagination and search
  useEffect(() => {
    const fetchWpsPayments = async () => {
      if (!id) return;

      try {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        };

        const wpsCondition = JSON.stringify({
          rules: [
            { field: 'establishment_id', operator: 'eq', value: id },
          ],
        });

        // Create search filter if search term exists
        let searchParams = '';
        if (searchTerm) {
          const searchFields = Object.keys(
            selectedColumns.wps
          ).filter((key) => selectedColumns.wps[key]);

          const globalFilter = JSON.stringify({
            searchTerm,
            searchFields: searchFields,
          });

          searchParams = `&globalFilter=${encodeURIComponent(
            globalFilter
          )}`;
        }

        const wpsResponse = await fetch(
          `https://services-sdp-dev.mohre.gov.ae/sdp/api/v1/vw_WPSCompliance?condition=${encodeURIComponent(
            wpsCondition
          )}&page=${pagination.wps.currentPage}&pageSize=${
            pagination.wps.pageSize
          }${searchParams}`,
          { headers }
        );
        if (wpsResponse.ok) {
          const wpsData = await wpsResponse.json();
          console.log('WPS compliance details response:', wpsData);
          if (wpsData.success && wpsData.data) {
            setWpsPayments(wpsData.data);
            setPagination((prev) => ({
              ...prev,
              wps: {
                ...prev.wps,
                total: wpsData.total || wpsData.data.length,
              },
            }));
            console.log('WPS payments set:', wpsData.data);
          } else {
            console.error('No WPS payments data found in response');
          }
        } else {
          console.error(
            'Failed to fetch WPS payments:',
            wpsResponse.status
          );
        }
      } catch (error) {
        console.error('Error fetching WPS payments:', error);
      }
    };

    fetchWpsPayments();
  }, [
    id,
    pagination.wps.currentPage,
    pagination.wps.pageSize,
    searchTerm,
  ]);

  // Fetch worker injuries with pagination and search
  useEffect(() => {
    const fetchWorkerInjuries = async () => {
      if (!id) return;

      try {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        };

        const condition = {
          rules: [
            {
              field: "establishment_id",
              operator: "eq",
              value: id,
            }
          ]
        };
        const encodedCondition = encodeURIComponent(JSON.stringify(condition));
        const injuryResponse = await fetch(
          `https://services-sdp-dev.mohre.gov.ae/jsonapi/transform/injury_list?` +
          `page=${pagination.workerInjury.currentPage}&pageSize=${pagination.workerInjury.pageSize}` +
          `&condition=${encodedCondition}`,
          { headers }
        );
        if (injuryResponse.ok) {
          const injuryData = await injuryResponse.json();
          console.log('Worker injuries response:', injuryData);
          if (injuryData.status && injuryData.data) {
            injuryData.data.forEach((injury: WorkerInjury) => {
              injury.insurance_company = 'Dubai Insurance';
              // Add 10 days to incident date
              const incidentDate = new Date(
                injury.params?.incident_date
                  ? injury.params?.incident_date
                  : '2025-04-06'
              );
              const claimDate = new Date(
                incidentDate.setDate(incidentDate.getDate() + 10)
              )
                .toISOString()
                .split('T')[0];
              injury.claim_date = claimDate;
              injury.comm_date = injury.params?.incident_date;
            });
            setWorkerInjuries(injuryData.data);
            setPagination((prev) => ({
              ...prev,
              workerInjury: {
                ...prev.workerInjury,
                total: injuryData.total || injuryData.data.length,
              },
            }));
            console.log('Worker injuries set:', injuryData.data);
          } else {
            console.error(
              'No worker injuries data found in response'
            );
          }
        } else {
          console.error(
            'Failed to fetch worker injuries:',
            injuryResponse.status
          );
        }
      } catch (error) {
        console.error('Error fetching worker injuries:', error);
      }
    };

    fetchWorkerInjuries();
  }, [
    id,
    pagination.workerInjury.currentPage,
    pagination.workerInjury.pageSize,
    searchTerm,
  ]);

  const resetBottomSheet = () => {
    setBottomSheetHeight('70%');
    setActiveTab('establishment');
  };

  // Format date for display
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString("en-In", { day: "numeric", month: "numeric", year: "numeric" })
      // return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  };

  // Format ymonth (format: 202502) to display as "Feb 2025"
  const formatYMonth = (ymonthString: string | null | undefined) => {
    if (!ymonthString) return '-';
    try {
      let year: string;
      let month: number;
      // Check if format is YYYY-MM (from the new API)
      if (ymonthString.includes('-')) {
        const parts = ymonthString.split('-');
        year = parts[0];
        month = parseInt(parts[1], 10);
      }
      // Check if format is YYYYMM (from the old API)
      else if (ymonthString.length === 6) {
        year = ymonthString.substring(0, 4);
        month = parseInt(ymonthString.substring(4, 6), 10);
      } else {
        return '-';
      }

      const date = new Date(parseInt(year), month - 1, 1);
      if (isNaN(date.getTime())) return '-';

      const monthName = date.toLocaleString('en-US', {
        month: 'short',
      });
      return `${monthName} ${year}`;
    } catch (error) {
      console.error('Error formatting ymonth:', error);
      return '-';
    }
  };

  // Format incident_date to local date string
  const formatIncidentDate = (
    dateString: string | null | undefined
  ) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting incident date:', error);
      return '-';
    }
  };

  // Mask card number to show only first two digits
  const maskCardNumber = (cardNumber: string | null | undefined) => {
    if (!cardNumber) return '-';
    if (cardNumber.length < 3) return cardNumber;
    return (
      cardNumber.substring(0, 2) + 'X'.repeat(cardNumber.length - 2)
    );
  };

  // Mask EID number to show only last 4 digits
  const maskEidNumber = (eidNumber: string | null | undefined) => {
    if (!eidNumber) return '-';
    if (eidNumber.length <= 4) return eidNumber;
    return 'X'.repeat(eidNumber.length - 4) + eidNumber.slice(-4);
  };

  // Log the task data for debugging
  useEffect(() => {
    if (currentTask) {
      console.log('Current task data:', currentTask);
    }
  }, [currentTask]);

  // Helper function to get the best available value from multiple possible field names
  const getFieldValue = (
    obj: any,
    fieldNames: string[],
    defaultValue: string = '-'
  ) => {
    for (const field of fieldNames) {
      if (
        obj[field] &&
        obj[field] !== '-' &&
        obj[field] !== 'null' &&
        obj[field] !== 'undefined'
      ) {
        return obj[field];
      }
    }
    return defaultValue;
  };

  // Use establishment data from task or fallback to mock data
  const establishment = currentTask?.establishment || {
    id: id,
    name_en: 'Loading...',
    name_ar: 'جاري التحميل...',
    establishment_name_en: 'Loading...',
    establishment_name_ar: 'جاري التحميل...',
    license_number: '-',
    license_no: '-',
    license_expiry_date: '-',
    expiry_date: '-',
    registration_date: '-',
    reg_date: '-',
    category: '-',
    category_name_en: '-',
    sector_en: '-',
    sector_ar: '-',
    sector_name_en: '-',
    sector_name_ar: '-',
    status: 'Active',
    status_name_en: 'Active',
    last_updated: '-',
    risk_grade: '-',
    risk_level: '-',
  };

  // Extract risk score from subject or incident description
  const extractRiskScore = () => {
    const subject = currentTask.params.subject || ""
    const match = subject.match(/Score: (\d+\.\d+)/)
    return match ? match[1] : "N/A"
  }

  return (
    <div
      className="flex flex-col min-h-screen"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
    >
      <Header
        title={
          language === 'en'
            ?
              'Inspection Details'
            :
              'تفاصيل المنشأة'
        }
        isRTL={language === 'ar'}
        showBackButton={true}
        backTo="/plan"
      />
      <div className="relative top-[4rem] flex-1 p-4 space-y-4 pb-30">
        {currentTask && (
          <div className="">
            <Card className="w-full border-none bg-gold-50 shadow-md">
              {/* Header with risk category */}
              <div className="border-b border-gold-500 p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-xl font-bold">{currentTask.establishment.name_en}</h2>
                    <p className="text-sm opacity-90">{currentTask.establishment.name_ar}</p>
                        </div>
                        <div className="flex items-center gap-2 bg-red-700 px-3 py-1 rounded-full text-white">

                      <span className="font-bold text-sm h-4 w-.5"></span>
                      {/*<AlertTriangle className="h-4 w-4" /> {currentTask.params.risk_category} RISK */}
                  </div>
                </div>
              </div>

              {/* Main content */}
              <div className="border-b border-gold-500 p-4 grid gap-4">
                {/* Key details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Building className="h-4 w-4 text-gold-600" />
                      <div>
                        <p className="text-sm font-medium">Establishment ID: {currentTask.establishment.id}</p>
                        <p className="text-xs text-gray-500">License: {currentTask.establishment.license_no}, Issue: {formatDate(currentTask.establishment.license_issue_date)}, Expiry: {formatDate(currentTask.establishment.license_expiry_date)}</p>
                      </div>
                      </div>
                      <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gold-600" />
                      <div>
                        <p className="text-sm font-medium">{currentTask.establishment.available_quota}</p>
                        <p className="text-xs text-gray-500">Available Quota</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gold-600" />
                      <div>
                        <p className="text-sm font-medium">{currentTask.establishment.allocated_quota}</p>
                        <p className="text-xs text-gray-500">Total Quotas</p>
                      </div>
                      </div>
                      <div className="flex items-center gap-2">
                      <Flag className="h-4 w-4 text-gold-600" />
                      <div>
                        <p className="text-sm font-medium">Nationality: {currentTask.establishment.nationality_en || "N/A"}</p>
                        <p className="text-xs text-gray-500">Location: {currentTask.establishment.latitude && currentTask.establishment.longitude
                                ? `${currentTask.establishment.latitude}, ${currentTask.establishment.longitude}`
                                : "N/A"}</p>
                      </div>
                    </div>
                    {/* <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gold-600" />
                      <div>
                        <p className="text-sm font-medium">Created by: {currentTask.created_by}</p>
                        <p className="text-xs text-gray-500">Pool: {currentTask.pool}</p>
                      </div>
                    </div> */}
                    {/* <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-gold-600" />
                      <div>
                        <p className="text-sm font-medium">{currentTask.params.inspection_type_display}</p>
                        <p className="text-xs text-gray-500">{currentTask.task_type_display}</p>
                      </div>
                    </div> */}
                  </div>
                  <div className="space-y-3">
                      <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-gold-600" />
                      <div>
                        <p className="text-sm font-medium">{currentTask.params.emirate_display}</p>
                        <p className="text-xs text-gray-500">{currentTask.params.zone_display}</p>
                      </div>
                      </div>
                      <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gold-600" />
                      <div>
                        <p className="text-sm font-medium">{currentTask.establishment.active_permit_count}</p>
                        <p className="text-xs text-gray-500">Used Quota</p>
                        </div>
                        </div>
                      <div className="flex items-center gap-2">
                      <Flag className="h-4 w-4 text-gold-600" />
                      <div>
                        <p className="text-sm font-medium">{currentTask.establishment.taqeem_status || "N/A"}</p>
                        <p className="text-xs text-gray-500">Taqeem Status</p>
                      </div>
                    </div>
                    {/* <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gold-600" />
                      <div>
                        <p className="text-sm font-medium">Reported: {formatDate(currentTask.params.reported_date)}</p>
                        <p className="text-xs text-gray-500">Started: {formatDate(currentTask.params.started_at)}</p>
                      </div>
                    </div> */}

                    {/* <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gold-600" />
                      <div>
                        <p className="text-sm font-medium">Incident Time: {currentTask.params.incident_time}</p>
                        <p className="text-xs text-gray-500">Transaction: {currentTask.transaction_id}</p>
                      </div>
                    </div> */}
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gold-600" />
                      <div>
                        <p className="text-sm font-medium">{currentTask.pool}</p>
                        <p className="text-xs text-gray-500">Pool </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

                {/* Footer */}
                <div className="bg-white rounded-b-lg w-full overflow-hidden shadow-md">
                  {taskList.map((cTask: TaskItem, index) => (
                    <div key={index} className="border p-3 border-gold-200 flex flex-col divide-y divide-gray-200">
                      <div className="flex justify-between items-center gap-2">
                        <div className="py-2 flex flex-row items-start gap-2">
                          <Inspect className="h-5 w-5 text-gold-600" />
                          <div className="flex flex-col items-start gap-2">
                            <div className="text-sm font-medium">{cTask.params?.inspection_type_display} <span className="ml-2 text-gray-500">(#{cTask.transaction_id})</span></div>
                            <div className="text-xs text-gray-500">{cTask.params?.incident_description} </div>
                            <div className="text-xs text-gray-500"><span className="text-black"> {cTask.task_status_display.toUpperCase()} </span>  &nbsp; -  {new Date(cTask.created_at).toLocaleDateString()} </div>
                          </div>
                        </div>
                          {cTask && cTask?.task_status_id === 14 && (
                            <Button
                              className="flex items-center justify-center gap-1 rounded-lg border border-[#b89256] bg-[#b89256] px-4 py-2 font-medium text-white transition-all focus:outline-none focus:ring"
                              onClick={() => {
                                // navigate(`/establishment/${cTask.id}/questionnaire`);
                                setIsBottomSheetExpanded(true);
                                setActiveTab('questionnaire');
                                return;
                              }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                                <path d="M4.5 3.75a3 3 0 00-3 3v.75h21v-.75a3 3 0 00-3-3h-15z" />
                                <path fillRule="evenodd" d="M22.5 9.75h-21v7.5a3 3 0 003 3h15a3 3 0 003-3v-7.5zm-18 3.75a.75.75 0 01.75-.75h6a.75.75 0 010 1.5h-6a.75.75 0 01-.75-.75zm.75 2.25a.75.75 0 000 1.5h3a.75.75 0 000-1.5h-3z" clipRule="evenodd" />
                              </svg>
                              <span>{t('common.startInspection')}</span>
                            </Button>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
            </Card>
          </div>
        )}

        {/* Inspection Questionnaire Start */}
        {questionnaireOpen && (
          <InspectionQuestionnairePage></InspectionQuestionnairePage>)}
        {/* Inspection Questionnaire End  */}

        {/* No action button at the bottom since we have it at the top */}
      </div>
      {true && (
        <div
            className={`absolute bottom-0 left-0 right-0 bg-white shadow-xl z-20 border border-gray-200 transition-all duration-300 ease-in-out`}
            style={{
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
              overflowY: isBottomSheetExpanded ? 'auto' : 'hidden',
              overflowX: 'hidden',
              height: isBottomSheetExpanded ? bottomSheetHeight : '100px',
            }}
          >
            <div
              className="w-full flex justify-center items-center pt-0 pb-4 cursor-pointer"
              onClick={() => {
                if (isBottomSheetExpanded) {
                  setIsBottomSheetExpanded(false);
                  resetBottomSheet();
                } else {
                  setIsBottomSheetExpanded(true);
                }
              }}
            >
              <div className="w-10 h-1 bg-gray-300 rounded-full mt-4"></div>
            </div>
            {/* Establishment Details Sections and Quessionaire Page */}
          <div className="flex flex-col gap-4 p-4 ">
            <div className="flex border-b border-gray-200">
              <button
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === 'establishment'
                    ? 'border-b-2 border-gold-600 text-gold-600'
                    : 'text-gray-500'
                }`}
                onClick={() => setActiveTab('establishment')}
              >
                {t('common.establishmentDetails')}
              </button>
              <button
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === 'questionnaire'
                    ? 'border-b-2 border-gold-600 text-gold-600'
                    : 'text-gray-500'
                }`}
                onClick={() => setActiveTab('questionnaire')}
              >
                {t('common.inspectionQuestionnaire')}
              </button>
            </div>
            <div className="mt-2">
              {activeTab === 'establishment' && <EstablishmentAccordionPage />}
              {activeTab === 'questionnaire' && <InspectionQuestionnairePage />}
            </div>
          </div>
        </div>
        )}
    </div>
  );
};

export default EstablishmentDetailPage;
